import { textCounts } from '@platform/schema/scene-fix';

import { editableItems, sceneBody, sceneFieldName, sceneHint } from './edits';

import type { FieldWrite, ReviewScene } from './edits';
import type { Step } from './history';
import type { FixSceneFieldResponse } from '@platform/client';
import type { FixField } from '@platform/schema/scene-fix';

/** The names Fix with AI and its Revert are recorded under on the action feed. */
export const FIX_WITH_AI = 'Fix with AI';
export const REVERT = 'Revert';

/** Shown for any fix that did not land, whatever the reason: nothing was changed. */
export const FIX_FAILED = 'Couldn’t fix this. Try again or edit it yourself.';

export interface FixTarget {
  lessonId: string;
  sceneId: string;
  field: FixField;
}

/** The field's text now, keyed as a fix keys it: by field name, or by option id. */
function fieldTexts(
  scene: ReviewScene,
  field: FixField,
): Record<string, string> {
  switch (field) {
    case 'items':
      return Object.fromEntries(
        editableItems(scene).map((item) => [item.id, item.label]),
      );
    case 'body':
      return { body: sceneBody(scene) };
    case 'hint':
      return { hint: sceneHint(scene) };
    case 'explanation':
      return {
        explanation: 'explanation' in scene ? (scene.explanation ?? '') : '',
      };
    case 'prompt':
      return { prompt: scene.prompt };
  }
}

/** Whether the field still holds what the fix wrote — Revert is offered only then. */
export const stillFixed = (scene: ReviewScene, fix: FixSceneFieldResponse) => {
  const now = fieldTexts(scene, fix.field);

  return fix.changes.every((change) => now[change.key] === change.after);
};

/**
 * The fix as an undo step: the text before it and after it, each as the write a hand
 * edit sends. Revert sends `before`, and Undo and Redo replay the pair.
 */
export function fixStep(
  scene: ReviewScene,
  target: FixTarget,
  fix: FixSceneFieldResponse,
): Step<FieldWrite> | undefined {
  const write = (side: 'before' | 'after'): FieldWrite | undefined => {
    const text = (key: string) =>
      fix.changes.find((change) => change.key === key)?.[side] ?? '';

    if (fix.field === 'explanation') {
      if (!('questionId' in scene) || scene.questionId === undefined) {
        return undefined;
      }
      return {
        kind: 'question',
        lessonId: target.lessonId,
        questionId: scene.questionId,
        explanation: text('explanation'),
      };
    }

    return {
      kind: 'scene',
      lessonId: target.lessonId,
      sceneId: target.sceneId,
      body:
        fix.field === 'items'
          ? {
              items: fix.changes.map((change) => ({
                id: change.key,
                label: change[side],
              })),
            }
          : { [fix.field]: text(fix.field) },
    };
  };

  const before = write('before');
  const after = write('after');
  if (before === undefined || after === undefined) return undefined;

  return {
    label: sceneFieldName(scene.position, fix.field),
    before,
    after,
  };
}

/** What the green box reports about text that now passes: "142 characters, 2 paragraphs". */
export function fixedCounts(scene: ReviewScene, field: FixField): string {
  if (field === 'items')
    return `${String(editableItems(scene).length)} options`;

  const { characters, paragraphs } = textCounts(
    Object.values(fieldTexts(scene, field)).join('\n'),
  );

  return `${String(characters)} ${characters === 1 ? 'character' : 'characters'}, ${String(paragraphs)} ${paragraphs === 1 ? 'paragraph' : 'paragraphs'}`;
}
